import React, { useEffect, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileArrowUp, PencilSimple, WarningCircle, FilePdf, FileImage, Trash, Plus, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { APPLIANCE_TYPES, EMPTY_APPLIANCE_FORM, itemToForm } from "@/lib/applianceTypes";

const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

function FieldWarning({ field, lowConfidence }) {
    if (!lowConfidence?.includes(field)) return null;
    return (
        <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1" data-testid={`appliance-warn-${field}`}>
            <WarningCircle size={11} /> Please verify this value
        </p>
    );
}

export default function AddApplianceDialog({ open, onOpenChange, flatId, editing, onSaved }) {
    const [step, setStep] = useState("choice"); // choice | uploading | failed | multi_select | form
    const [form, setForm] = useState(EMPTY_APPLIANCE_FORM);
    const [fileId, setFileId] = useState("");
    const [lowConfidence, setLowConfidence] = useState([]);
    const [reviewMode, setReviewMode] = useState(false);
    const [failMessage, setFailMessage] = useState("");
    const [extractionItems, setExtractionItems] = useState([]);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [services, setServices] = useState([]);
    const [newService, setNewService] = useState({ service_date: "", description: "", cost: "", performed_by: "", notes: "" });
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setForm({
                appliance_type: editing.appliance_type || "other",
                other_type_label: editing.other_type_label || "",
                brand: editing.brand || "", model: editing.model || "", serial_number: editing.serial_number || "",
                description: editing.description || "", purchase_date: editing.purchase_date || "",
                invoice_number: editing.invoice_number || "", purchase_price: editing.purchase_price != null ? String(editing.purchase_price) : "",
                seller_name: editing.seller_name || "", seller_contact: editing.seller_contact || "",
                warranty_duration: editing.warranty_duration || "", warranty_start_date: editing.warranty_start_date || "",
                warranty_end_date: editing.warranty_end_date || "", warranty_provider: editing.warranty_provider || "",
                installation_date: editing.installation_date || "", amc_provider: editing.amc_provider || "",
                amc_start_date: editing.amc_start_date || "", amc_end_date: editing.amc_end_date || "",
                amc_cost: editing.amc_cost != null ? String(editing.amc_cost) : "", notes: editing.notes || "",
                invoice_file_id: editing.invoice_file_id || "",
            });
            setFileId(editing.invoice_file_id || "");
            setReviewMode(false);
            setLowConfidence([]);
            setStep("form");
            loadServices(editing.id);
        } else {
            setForm(EMPTY_APPLIANCE_FORM);
            setFileId(""); setLowConfidence([]); setReviewMode(false);
            setExtractionItems([]); setServices([]);
            setStep("choice");
        }
    }, [open, editing]);

    async function loadServices(aid) {
        try {
            const { data } = await api.get(`/appliances/${aid}/services`);
            setServices(data || []);
        } catch (e) { toast.error(formatError(e)); }
    }

    function applyItem(item, fid) {
        setForm({ ...itemToForm(item), invoice_file_id: fid });
        setLowConfidence(item.low_confidence_fields || []);
        setReviewMode(true);
        setStep("form");
    }

    function pickFile() { fileInputRef.current?.click(); }

    function onReplaceInvoiceClick() {
        if (!window.confirm("Replace existing purchase invoice? The new invoice will be read and shown for you to review before saving.")) return;
        pickFile();
    }

    async function handleFileChosen(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!ALLOWED_MIME.includes(file.type)) {
            toast.error("Unsupported file type. Please upload a PDF, JPG, PNG or WEBP invoice.");
            return;
        }
        if (file.size > MAX_BYTES) {
            toast.error("File is too large. Please upload a file under 10 MB.");
            return;
        }
        setStep("uploading");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/flats/${flatId}/appliances/extract-invoice`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setFileId(data.file_id);
            if (data.extraction_status !== "success" || !data.items?.length) {
                setFailMessage(data.message || "We couldn't read the invoice automatically. You can enter the appliance details manually.");
                setStep("failed");
                return;
            }
            setExtractionItems(data.items);
            if (data.items.length > 1) {
                setSelectedIdx(0);
                setStep("multi_select");
            } else {
                applyItem(data.items[0], data.file_id);
            }
        } catch (err) {
            toast.error(formatError(err));
            setStep(editing ? "form" : "choice");
        }
    }

    function useManualAfterFailure() {
        if (editing) {
            setForm((f) => ({ ...f, invoice_file_id: fileId || f.invoice_file_id }));
        } else {
            setForm({ ...EMPTY_APPLIANCE_FORM, invoice_file_id: fileId || "" });
        }
        setReviewMode(false);
        setLowConfidence([]);
        setStep("form");
    }

    async function viewInvoice() {
        if (!editing?.id) return;
        try {
            const res = await api.get(`/appliances/${editing.id}/invoice/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            window.open(url, "_blank");
        } catch (err) { toast.error(formatError(err)); }
    }

    async function handleSave(e) {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload = { ...form };
            payload.purchase_price = payload.purchase_price === "" ? null : Number(payload.purchase_price);
            payload.amc_cost = payload.amc_cost === "" ? null : Number(payload.amc_cost);
            Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
            if (editing) {
                await api.patch(`/appliances/${editing.id}`, payload);
                toast.success("Appliance updated");
            } else {
                await api.post(`/flats/${flatId}/appliances`, payload);
                toast.success("Appliance saved");
            }
            onOpenChange(false);
            onSaved();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    }

    async function addService(e) {
        e.preventDefault();
        if (!newService.service_date || !newService.description) return;
        try {
            const payload = { ...newService, cost: newService.cost === "" ? null : Number(newService.cost) };
            await api.post(`/appliances/${editing.id}/services`, payload);
            setNewService({ service_date: "", description: "", cost: "", performed_by: "", notes: "" });
            loadServices(editing.id);
            toast.success("Service log added");
        } catch (err) { toast.error(formatError(err)); }
    }

    async function removeService(sid) {
        if (!window.confirm("Delete this service log entry?")) return;
        try { await api.delete(`/appliance-services/${sid}`); loadServices(editing.id); }
        catch (err) { toast.error(formatError(err)); }
    }

    function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto">
                <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                    data-testid="appliance-file-input" onChange={handleFileChosen} />

                {step === "choice" && (
                    <>
                        <DialogHeader>
                            <DialogTitle className="font-heading text-2xl tracking-tight">Add appliance</DialogTitle>
                        </DialogHeader>
                        <p className="text-sm text-brand-inkSoft mb-2">How would you like to add it?</p>
                        <div className="grid grid-cols-1 gap-3">
                            <button data-testid="appliance-choice-upload" onClick={pickFile}
                                className="text-left border border-brand-line rounded-sm p-4 hover:border-brand-action hover:bg-brand-bg transition-colors">
                                <p className="text-sm font-medium text-brand-ink flex items-center gap-2"><FileArrowUp size={17} weight="duotone" /> Upload Purchase Invoice</p>
                                <p className="text-xs text-brand-inkSoft mt-1">Auto-fill details from your invoice (PDF, JPG, PNG, WEBP)</p>
                            </button>
                            <button data-testid="appliance-choice-manual" onClick={() => { setForm(EMPTY_APPLIANCE_FORM); setReviewMode(false); setLowConfidence([]); setStep("form"); }}
                                className="text-left border border-brand-line rounded-sm p-4 hover:border-brand-action hover:bg-brand-bg transition-colors">
                                <p className="text-sm font-medium text-brand-ink flex items-center gap-2"><PencilSimple size={16} /> Add Manually</p>
                                <p className="text-xs text-brand-inkSoft mt-1">Enter appliance details yourself</p>
                            </button>
                        </div>
                    </>
                )}

                {step === "uploading" && (
                    <div className="py-10 text-center" data-testid="appliance-uploading">
                        <Spinner size={28} className="animate-spin mx-auto text-brand-action" />
                        <p className="text-sm text-brand-ink mt-4 font-medium">Reading your invoice…</p>
                        <p className="text-xs text-brand-inkSoft mt-1">This can take a few seconds. Please don't close this dialog.</p>
                    </div>
                )}

                {step === "failed" && (
                    <div className="py-6" data-testid="appliance-failed-message">
                        <p className="text-sm text-brand-ink font-medium mb-2">We couldn't read this invoice automatically</p>
                        <p className="text-sm text-brand-inkSoft mb-5">{failMessage}</p>
                        <Button data-testid="appliance-manual-entry-btn" onClick={useManualAfterFailure}
                            className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                            Enter Details Manually
                        </Button>
                    </div>
                )}

                {step === "multi_select" && (
                    <>
                        <DialogHeader>
                            <DialogTitle className="font-heading text-xl tracking-tight">We found multiple products on this invoice</DialogTitle>
                        </DialogHeader>
                        <p className="text-sm text-brand-inkSoft mb-3">Select which one you'd like to add now. You can add the others afterwards from this same invoice.</p>
                        <div className="space-y-2 mb-4" data-testid="appliance-multi-item-list">
                            {extractionItems.map((it, i) => (
                                <label key={i} data-testid={`appliance-item-checkbox-${i}`}
                                    className={`flex items-center gap-3 border rounded-sm p-3 cursor-pointer ${selectedIdx === i ? "border-brand-action bg-brand-bg" : "border-brand-line"}`}>
                                    <input type="radio" name="appliance-item" checked={selectedIdx === i} onChange={() => setSelectedIdx(i)} />
                                    <div className="min-w-0">
                                        <p className="text-sm text-brand-ink font-medium truncate">{it.brand} {it.model || it.description || "Item"}</p>
                                        <p className="text-xs text-brand-inkSoft">{it.description || it.appliance_type_label || "Unclassified item"}</p>
                                    </div>
                                </label>
                            ))}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                            <Button data-testid="appliance-multi-continue-btn" onClick={() => applyItem(extractionItems[selectedIdx], fileId)}
                                className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">Continue</Button>
                        </DialogFooter>
                    </>
                )}

                {step === "form" && (
                    <>
                        <DialogHeader>
                            <DialogTitle className="font-heading text-2xl tracking-tight">
                                {editing ? "Edit appliance" : "Review details"}
                            </DialogTitle>
                        </DialogHeader>
                        {reviewMode && (
                            <p className="text-sm text-brand-inkSoft mb-3" data-testid="appliance-review-banner">
                                We found the following information from your invoice. Please verify before saving.
                            </p>
                        )}
                        <form onSubmit={handleSave} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Appliance type</Label>
                                    <Select value={form.appliance_type} onValueChange={(v) => set("appliance_type", v)}>
                                        <SelectTrigger data-testid="appliance-type-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                        <SelectContent>{APPLIANCE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <FieldWarning field="appliance_type" lowConfidence={lowConfidence} />
                                </div>
                                {form.appliance_type === "other" && (
                                    <div className="space-y-2">
                                        <Label>Describe the appliance</Label>
                                        <Input data-testid="appliance-other-type-input" value={form.other_type_label}
                                            onChange={(e) => set("other_type_label", e.target.value)} className="rounded-sm border-brand-line" />
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Brand</Label>
                                    <Input data-testid="appliance-brand-input" value={form.brand} onChange={(e) => set("brand", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Model</Label>
                                    <Input data-testid="appliance-model-input" value={form.model} onChange={(e) => set("model", e.target.value)} className="rounded-sm border-brand-line" />
                                    <FieldWarning field="model" lowConfidence={lowConfidence} />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Serial number</Label>
                                <Input data-testid="appliance-serial-input" value={form.serial_number}
                                    placeholder={reviewMode && !form.serial_number ? "Not detected — enter manually" : ""}
                                    onChange={(e) => set("serial_number", e.target.value)} className="rounded-sm border-brand-line font-mono" />
                                <FieldWarning field="serial_number" lowConfidence={lowConfidence} />
                            </div>
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft pt-2">Purchase information</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Purchase date</Label>
                                    <Input data-testid="appliance-purchase-date-input" type="date" value={form.purchase_date}
                                        onChange={(e) => set("purchase_date", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Invoice number</Label>
                                    <Input data-testid="appliance-invoice-number-input" value={form.invoice_number}
                                        onChange={(e) => set("invoice_number", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Purchase price (₹)</Label>
                                    <Input data-testid="appliance-purchase-price-input" type="number" step="0.01" value={form.purchase_price}
                                        onChange={(e) => set("purchase_price", e.target.value)} className="rounded-sm border-brand-line" />
                                    <FieldWarning field="purchase_price" lowConfidence={lowConfidence} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Seller / dealer</Label>
                                    <Input data-testid="appliance-seller-input" value={form.seller_name}
                                        onChange={(e) => set("seller_name", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Seller contact (optional)</Label>
                                <Input data-testid="appliance-seller-contact-input" value={form.seller_contact}
                                    onChange={(e) => set("seller_contact", e.target.value)} className="rounded-sm border-brand-line" />
                            </div>
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft pt-2">Warranty (optional)</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Warranty duration</Label>
                                    <Input data-testid="appliance-warranty-duration-input" value={form.warranty_duration}
                                        placeholder="e.g. 1 Year" onChange={(e) => set("warranty_duration", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Warranty provider</Label>
                                    <Input data-testid="appliance-warranty-provider-input" value={form.warranty_provider}
                                        onChange={(e) => set("warranty_provider", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Warranty start</Label>
                                    <Input data-testid="appliance-warranty-start-input" type="date" value={form.warranty_start_date}
                                        onChange={(e) => set("warranty_start_date", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Warranty end</Label>
                                    <Input data-testid="appliance-warranty-end-input" type="date" value={form.warranty_end_date}
                                        onChange={(e) => set("warranty_end_date", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Installation date (optional)</Label>
                                <Input data-testid="appliance-installation-date-input" type="date" value={form.installation_date}
                                    onChange={(e) => set("installation_date", e.target.value)} className="rounded-sm border-brand-line" />
                            </div>
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft pt-2">AMC (optional)</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>AMC provider</Label>
                                    <Input data-testid="appliance-amc-provider-input" value={form.amc_provider}
                                        onChange={(e) => set("amc_provider", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                                <div className="space-y-2">
                                    <Label>AMC cost (₹)</Label>
                                    <Input data-testid="appliance-amc-cost-input" type="number" step="0.01" value={form.amc_cost}
                                        onChange={(e) => set("amc_cost", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>AMC start</Label>
                                    <Input data-testid="appliance-amc-start-input" type="date" value={form.amc_start_date}
                                        onChange={(e) => set("amc_start_date", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                                <div className="space-y-2">
                                    <Label>AMC end</Label>
                                    <Input data-testid="appliance-amc-end-input" type="date" value={form.amc_end_date}
                                        onChange={(e) => set("amc_end_date", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Notes (optional)</Label>
                                <Textarea data-testid="appliance-notes-input" rows={2} value={form.notes}
                                    onChange={(e) => set("notes", e.target.value)} className="rounded-sm border-brand-line" />
                            </div>

                            {/* Purchase document */}
                            <div className="border border-brand-line rounded-sm p-3">
                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-2">Purchase document</p>
                                {form.invoice_file_id ? (
                                    <div className="flex items-center justify-between gap-2">
                                        <p className="text-sm text-brand-ink flex items-center gap-2"><FilePdf size={16} weight="duotone" /> Purchase invoice attached</p>
                                        <div className="flex items-center gap-3">
                                            {editing && <button type="button" data-testid="appliance-view-invoice-btn" onClick={viewInvoice} className="text-xs text-brand-action hover:underline">View</button>}
                                            <button type="button" data-testid="appliance-replace-invoice-btn" onClick={editing ? onReplaceInvoiceClick : pickFile} className="text-xs text-brand-inkSoft hover:text-brand-action hover:underline">Replace</button>
                                        </div>
                                    </div>
                                ) : (
                                    <button type="button" data-testid="appliance-attach-invoice-btn" onClick={pickFile}
                                        className="text-xs text-brand-action hover:underline inline-flex items-center gap-1"><FileImage size={13} /> Attach purchase invoice</button>
                                )}
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="appliance-cancel-btn">Cancel</Button>
                                <Button type="submit" disabled={submitting} data-testid="appliance-save-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                                    {submitting ? "Saving…" : (editing ? "Save" : "Save Appliance")}
                                </Button>
                            </DialogFooter>
                        </form>

                        {editing && (
                            <div className="mt-6 border-t border-brand-line pt-4">
                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3">Service history</p>
                                <div className="space-y-2 mb-3">
                                    {services.length === 0 && <p className="text-xs text-brand-inkSoft">No service logs yet.</p>}
                                    {services.map((s) => (
                                        <div key={s.id} data-testid={`appliance-service-${s.id}`} className="flex items-center justify-between border border-brand-line rounded-sm p-2 text-sm">
                                            <div>
                                                <p className="text-brand-ink">{s.description}</p>
                                                <p className="text-xs text-brand-inkSoft">{s.service_date}{s.cost ? ` · ₹${s.cost}` : ""}{s.performed_by ? ` · ${s.performed_by}` : ""}</p>
                                            </div>
                                            <button type="button" data-testid={`appliance-delete-service-${s.id}`} onClick={() => removeService(s.id)} className="p-1 text-brand-action hover:bg-[#FEF2F2] rounded-sm"><Trash size={13} /></button>
                                        </div>
                                    ))}
                                </div>
                                <form onSubmit={addService} className="grid grid-cols-3 gap-2">
                                    <Input data-testid="appliance-service-date-input" type="date" required value={newService.service_date}
                                        onChange={(e) => setNewService((s) => ({ ...s, service_date: e.target.value }))} className="rounded-sm border-brand-line text-xs" />
                                    <Input data-testid="appliance-service-desc-input" required placeholder="e.g. Gas refill" value={newService.description}
                                        onChange={(e) => setNewService((s) => ({ ...s, description: e.target.value }))} className="rounded-sm border-brand-line text-xs" />
                                    <div className="flex gap-1">
                                        <Input data-testid="appliance-service-cost-input" type="number" placeholder="Cost" value={newService.cost}
                                            onChange={(e) => setNewService((s) => ({ ...s, cost: e.target.value }))} className="rounded-sm border-brand-line text-xs" />
                                        <Button type="submit" size="icon" data-testid="appliance-add-service-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover shrink-0"><Plus size={14} /></Button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
